import React from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Heading,
  Input,
  List,
  ListItem,
  VStack,
  HStack,
  IconButton,
  Badge,
  FormControl,
  FormLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Card,
  CardHeader,
  CardBody,
  useColorModeValue,
  Divider,
  Tooltip,
} from '@chakra-ui/react';
import {
  Plus,
  PencilSimpleLine,
  XCircle,
  Star,
  Clock,
  Buildings,
  ClipboardText,
  Flag,
  CalendarBlank,
  ArrowRight
} from '@phosphor-icons/react';
import SourceLinkedText from './SourceLinkedText';
import { parseEra, displayEra } from '../../utils/eraUtils'; // Import era utilities

export default function SiteTimeline({
  site,
  editedSite,
  isEditing,
  editingPeriodIndex,
  onSetEditingPeriod,
  onUpdatePeriod,
  onAddPeriod,
  onSetMainPeriod,
  onDeletePeriod,
  onAddFeature,
  onRemoveFeature,
  showCitations // Added prop
}) {
  // Theme colors and styles
  const bgColor = useColorModeValue('bg.surface', 'bg.surface');
  const borderColor = useColorModeValue('border.default', 'border.default');
  const highlightBg = useColorModeValue('rgba(193, 148, 52, 0.1)', 'rgba(214, 173, 96, 0.1)');
  const accentColor = useColorModeValue('accent.gold', 'accent.gold');
  const infoColor = useColorModeValue('text.muted', 'text.muted');
  const connectorBg = useColorModeValue('rgba(193, 148, 52, 0.3)', 'rgba(214, 173, 96, 0.3)');
  const featureBg = useColorModeValue('rgba(193, 148, 52, 0.05)', 'rgba(214, 173, 96, 0.05)');

  const handlePeriodChange = (index, field, value) => {
    const updatedPeriods = [...(editedSite.constructionPeriods || [])];
    updatedPeriods[index] = {
      ...updatedPeriods[index],
      [field]: value
    };
    onUpdatePeriod(updatedPeriods);
  };

  return (
    <Box
      position="relative"
      borderRadius="md"
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      overflow="hidden"
      _after={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.05,
        pointerEvents: 'none',
        bgImage: 'var(--texture-parchment)',
        bgRepeat: 'repeat',
        bgSize: '500px',
        mixBlendMode: 'multiply',
      }}
    >
      <Box
        py={3}
        px={4}
        borderBottomWidth="1px"
        borderBottomColor={borderColor}
        bg={useColorModeValue('bg.accent', 'bg.accent')}
        fontFamily="heading"
        as={Flex}
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading 
          as="h3" 
          size="md"
          fontFamily="heading"
          color="text.default"
        >
           Timeline
        </Heading>
        {isEditing && (
          <Button
            size="sm"
            variant="journal"
            color={accentColor}
            borderColor={accentColor}
            leftIcon={<Plus size={16} />}
            onClick={onAddPeriod}
            _hover={{ bg: highlightBg }}
          >
            Add Period
          </Button>
        )}
      </Box>
      <Box p={4}>
        <Text fontSize="sm" mb={4} fontStyle="italic" color={infoColor}>
          Notable construction phases throughout the site's history
        </Text>
        
        {/* Visual Timeline Element */}
        <Box
          mb={6}
          p={3}
          borderRadius="md"
          bg="bg.surface" // Use semantic token instead of hardcoded gray
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <Flex align="center" justify="center" wrap="wrap" position="relative">
            {site.constructionPeriods && site.constructionPeriods.length > 0 && (
              site.constructionPeriods.map((period, index) => {
                const isMainPeriod = index === (site.mainPeriodIndex ?? 0);
                
                // Use displayEra utility for consistent formatting
                const startDisplay = displayEra(period.startEra, false); // Show year for 'present'
                const endDisplay = displayEra(period.endEra, true); // Show 'Present' text
                
                return (
                  <Tooltip
                    key={index}
                    label={`${period.periodName || `Period ${index + 1}`}: ${startDisplay || '?'} to ${endDisplay || '?'}`}
                    placement="top"
                  >
                    <Box
                      mx={2}
                      mb={2}
                      p={2}
                      minW="100px"
                      textAlign="center"
                      borderRadius="md"
                      bg={isMainPeriod ? highlightBg : featureBg}
                      borderWidth="1px"
                      borderColor={isMainPeriod ? accentColor : borderColor}
                      cursor="pointer"
                      onClick={() => document.getElementById(`timeline-period-${index}`).click()}
                      position="relative"
                      _hover={{
                        boxShadow: "md",
                        transform: "translateY(-2px)",
                        transition: "all 0.2s ease-in-out"
                      }}
                      transition="all 0.2s ease-in-out"
                    >
                      <Text fontSize="xs" fontWeight={isMainPeriod ? "bold" : "medium"} noOfLines={1}>
                        {period.periodName || `Period ${index + 1}`}
                      </Text>
                      <Text fontSize="xs" color={infoColor} whiteSpace="nowrap">
                        {startDisplay || '?'} {endDisplay && startDisplay !== endDisplay ? <ArrowRight size={8} style={{display: 'inline'}} /> : ''} {endDisplay && startDisplay !== endDisplay ? endDisplay : ''}
                      </Text>
                      
                      {/* Period connector line */}
                      {index < site.constructionPeriods.length - 1 && (
                        <Box
                          position="absolute"
                          right="-12px"
                          top="50%"
                          w="24px"
                          h="2px"
                          bg={connectorBg}
                          zIndex={0}
                        />
                      )}
                      
                      {/* Main period indicator */}
                      {isMainPeriod && (
                        <Badge
                          position="absolute"
                          top="-10px"
                          right="-5px"
                          colorScheme="yellow"
                          size="xs"
                        >
                          <Star size={10} weight="fill" />
                        </Badge>
                      )}
                    </Box>
                  </Tooltip>
                );
              })
            )}
          </Flex>
        </Box>
        {site.constructionPeriods && site.constructionPeriods.length > 0 ? (
          <Accordion allowToggle defaultIndex={[0]}>
            {site.constructionPeriods.map((period, index) => {
              const isMainPeriod = index === (site.mainPeriodIndex ?? 0);
              // Define display variables within this scope as well
              const startDisplay = displayEra(period.startEra, false); // Show year for 'present'
              const endDisplay = displayEra(period.endEra, true); // Show 'Present' text
              
              return (
                <AccordionItem
                  key={index}
                  border="none"
                  mb={3}
                  bg={bgColor}
                  boxShadow="sm"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderLeft={isMainPeriod ? "4px solid" : "1px solid"}
                  borderLeftColor={isMainPeriod ? accentColor : borderColor}
                  position="relative"
                  _after={isMainPeriod ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    pointerEvents: 'none',
                    bgImage: 'var(--texture-parchment)',
                    bgRepeat: 'repeat',
                    bgBlendMode: 'multiply'
                  } : {}}
                  overflow="hidden"
                  id={`timeline-period-${index}`}
                >
                  <AccordionButton 
                    py={3} 
                    px={4}
                    borderTopRadius="md"
                    _hover={{ bg: highlightBg }}
                    bg={isMainPeriod ? highlightBg : "transparent"}
                  >
                    <Box flex="1" textAlign="left">
                      <Flex align="center">
                        {isMainPeriod && (
                          <Badge 
                            variant="subtle" 
                            bg={accentColor}
                            color="white" 
                            mr={2}
                            px={2}
                            py={1}
                            borderRadius="md"
                            fontSize="xs"
                            fontWeight="bold"
                            textTransform="uppercase"
                            letterSpacing="wider"
                          >
                            Main Phase
                          </Badge>
                        )}
                        <Text
                          fontFamily="heading"
                          color={isMainPeriod ? accentColor : "text.default"}
                          fontSize="md"
                          fontWeight={isMainPeriod ? "bold" : "medium"}
                        >
                          {period.periodName || `Period ${index + 1}`}
                        </Text>
                      </Flex>
                      <HStack
                        fontSize="sm"
                        fontFamily="body"
                        color={infoColor}
                        mt={1}
                        spacing={2}
                        flexWrap="wrap"
                      >
                        <Flex align="center" minW="120px">
                          <Clock size={12} style={{marginRight: "4px"}} />
                          <Text fontStyle="italic">
                            {startDisplay || "?"} {endDisplay ? `to ${endDisplay}` : ""}
                          </Text>
                        </Flex>
                        
                        {period.culture && (
                          <Flex align="center">
                            <Buildings size={12} style={{marginRight: "4px"}} />
                            <Text fontStyle="italic">{period.culture}</Text>
                          </Flex>
                        )}
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} px={4}>
                    {isEditing && editingPeriodIndex === index ? (
                      <VStack spacing={3} align="stretch">
                        <FormControl>
                          <FormLabel fontSize="sm" color="ancient.stone">Period Name</FormLabel>
                          <Input
                            size="sm"
                            variant="carved"
                            value={editedSite.constructionPeriods[index].periodName || ''}
                            onChange={(e) => handlePeriodChange(index, 'periodName', e.target.value)}
                          />
                        </FormControl>
                        <HStack spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm" color="ancient.stone">Start Era</FormLabel>
                            <Input
                              size="sm"
                              value={editedSite.constructionPeriods[index].startEra || ''}
                              onChange={(e) => handlePeriodChange(index, 'startEra', e.target.value)}
                              placeholder="e.g. 3000 BCE"
                              variant="carved"
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm" color="ancient.stone">End Era</FormLabel>
                            <Input
                              size="sm"
                              value={editedSite.constructionPeriods[index].endEra || ''}
                              onChange={(e) => handlePeriodChange(index, 'endEra', e.target.value)}
                              placeholder="e.g. 2500 BCE"
                              variant="carved"
                            />
                          </FormControl>
                        </HStack>
                        <FormControl>
                          <FormLabel fontSize="sm" color="ancient.stone">Culture</FormLabel>
                          <Input
                            size="sm"
                            value={editedSite.constructionPeriods[index].culture || ''}
                            onChange={(e) => handlePeriodChange(index, 'culture', e.target.value)}
                            placeholder="e.g. Neolithic"
                            variant="carved"
                          />
                        </FormControl>
                        
                        <Box mt={2}>
                          <Flex justify="space-between">
                            <FormLabel fontSize="sm" mb={2} color="ancient.stone">Features</FormLabel>
                            <HStack>
                              {!isMainPeriod && (
                                <Button
                                  size="xs"
                                  variant="outline"
                                  color={accentColor}
                                  borderColor={accentColor}
                                  leftIcon={<Star size={12} />}
                                  onClick={() => onSetMainPeriod(index)}
                                  _hover={{ bg: highlightBg }}
                                >
                                  Set as Main Phase
                                </Button>
                              )}
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  color="red.500"
                                  onClick={() => onDeletePeriod(index)}
                                >
                                  Delete Period
                                </Button>
                            </HStack>
                          </Flex>
                          
                          {period.features && period.features.length > 0 && (
                            <List spacing={1} mb={2}>
                              {period.features.map((feature, featureIndex) => (
                                <ListItem key={featureIndex} display="flex" alignItems="center" justifyContent="space-between">
                                  <Text fontSize="sm" color="text.default">• {feature}</Text>
                                  <IconButton
                                    size="xs"
                                    icon={<XCircle size={12} />}
                                    aria-label="Remove feature"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => onRemoveFeature(index, featureIndex)}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                          
                          <Flex mt={2}>
                            <Input
                              placeholder="Add a feature..."
                              variant="carved"
                              size="sm"
                              mr={2}
                              id={`feature-input-${index}`}
                            />
                            <Button
                              size="sm"
                              variant="journal"
                              color={accentColor}
                              borderColor={accentColor}
                              _hover={{ bg: highlightBg }}
                              onClick={() => {
                                const input = document.getElementById(`feature-input-${index}`);
                                if (input && input.value) {
                                  onAddFeature(index, input.value);
                                  input.value = '';
                                }
                              }}
                            >
                              Add
                            </Button>
                          </Flex>
                        </Box>
                      </VStack>
                    ) : (
                      <>
                        {/* Display Period Details/Description */}
                        {period.details && (
                          <Box mb={period.features && period.features.length > 0 ? 4 : 2}>
                            <HStack mb={1}>
                              <ClipboardText size={16} color={accentColor} />
                              <Text fontWeight="medium" color="text.default" fontFamily="heading" fontSize="md">
                                Description:
                              </Text>
                            </HStack>
                            <Box pl={2} fontSize="sm" color="text.default" whiteSpace="pre-wrap">
                              <SourceLinkedText
                                text={period.details}
                                citations={site.citations || []}
                                groundingMetadata={site.groundingMetadata}
                                showCitations={showCitations} // Pass prop
                              />
                            </Box>
                          </Box>
                        )}

                        {period.features && period.features.length > 0 ? (
                          <>
                            <Flex justify="space-between" align="center">
                              <HStack mb={2}>
                                <Flag size={16} color={accentColor} weight="duotone" />
                                <Text
                                  fontWeight="medium"
                                  color="text.default"
                                  fontFamily="heading"
                                  fontSize="md"
                                >
                                  Notable Features:
                                </Text>
                              </HStack>
                              {isEditing && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  leftIcon={<PencilSimpleLine size={12} />}
                                  onClick={() => onSetEditingPeriod(index)}
                                >
                                  Edit
                                </Button>
                              )}
                            </Flex>
                            
                            <Box
                              p={3}
                              borderRadius="md"
                              bg={featureBg}
                              borderWidth="1px"
                              borderColor={borderColor}
                              position="relative"
                              _before={{
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.05,
                                pointerEvents: 'none',
                                bgImage: 'var(--texture-parchment)',
                                bgRepeat: 'repeat',
                                bgSize: '300px'
                              }}
                            >
                              <List spacing={3}>
                                {period.features.map((feature, featureIndex) => (
                                  <ListItem
                                    key={featureIndex}
                                    display="flex"
                                    alignItems="flex-start"
                                    mb={2}
                                    pb={featureIndex < period.features.length - 1 ? 2 : 0}
                                    borderBottom={featureIndex < period.features.length - 1 ? "1px solid" : "none"}
                                    borderColor="rgba(193, 148, 52, 0.2)"
                                  >
                                    <Box
                                      as="span"
                                      mr={2}
                                      color={accentColor}
                                      fontSize="sm"
                                      mt="3px"
                                    >
                                      {/* Using a generic icon for features here, can be changed */}
                                      <Star size={14} weight="duotone" />
                                    </Box>
                                    <Box
                                      color="text.default"
                                      fontWeight="medium"
                                      fontSize="md"
                                      fontFamily="body"
                                    >
                                      <SourceLinkedText
                                        text={feature}
                                        groundingMetadata={site.groundingMetadata}
                                        citations={site.citations || []} // Pass manual citations
                                        showCitations={showCitations} // Pass prop
                                      />
                                    </Box>
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          </>
                        ) : (
                          <>
                            {!period.details && ( /* Only show "No specific features" if details are also missing */
                              <Flex justify="space-between" align="center">
                                <Text textStyle="subtle" fontStyle="italic">No specific features or description recorded for this period.</Text>
                                {isEditing && (
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    leftIcon={<PencilSimpleLine size={12} />}
                                    onClick={() => onSetEditingPeriod(index)}
                                  >
                                    Edit
                                  </Button>
                                )}
                              </Flex>
                            )}
                             {/* If only features are missing, but details are present, the edit button might be desired elsewhere or not at all if details are shown */}
                             {period.details && !(period.features && period.features.length > 0) && isEditing && (
                                <Flex justify="flex-end" mt={2}>
                                   <Button
                                    size="xs"
                                    variant="ghost"
                                    leftIcon={<PencilSimpleLine size={12} />}
                                    onClick={() => onSetEditingPeriod(index)}
                                  >
                                    Edit Period
                                  </Button>
                                </Flex>
                             )}
                          </>
                        )}
                      </>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        ) : (
          <Box 
            py={6}
            textAlign="center"
            bg={highlightBg}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.05,
              pointerEvents: 'none',
              bgImage: 'var(--texture-parchment)',
              bgRepeat: 'repeat',
              bgSize: '300px'
            }}
          >
            <VStack spacing={2}>
              <CalendarBlank size={32} color={accentColor} weight="duotone" />
              <Text color={infoColor} fontStyle="italic">No timeline information available for this site.</Text>
              {isEditing && (
                <Button
                  size="sm"
                  variant="journal"
                  color={accentColor}
                  borderColor={accentColor}
                  leftIcon={<Plus size={16} />}
                  onClick={onAddPeriod}
                  mt={2}
                >
                  Add Period
                </Button>
              )}
            </VStack>
          </Box>
        )}
      </Box>
    </Box>
  );
}
